import { z } from "zod";

export const storyFields = [
  "aboutThisWork",
  "aCloserLook",
  "displayPossibilities",
  "fromTheCollection"
] as const;
export type StoryField = (typeof storyFields)[number];
export const storyLabels: Record<StoryField, string> = {
  aboutThisWork: "About This Work",
  aCloserLook: "A Closer Look",
  displayPossibilities: "Display and Creative Possibilities",
  fromTheCollection: "From the Collection"
};
export const storyFieldSchema = z.enum(storyFields);
export const storyTextSchema = z.string().max(6000);
export const storyValuesSchema = z
  .object({
    aboutThisWork: storyTextSchema,
    aCloserLook: storyTextSchema,
    displayPossibilities: storyTextSchema,
    fromTheCollection: storyTextSchema
  })
  .strict();
export type StoryValues = z.infer<typeof storyValuesSchema>;
export const storyHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  text: storyTextSchema,
  createdAt: z.string()
});
export type StoryHistoryEntry = z.infer<typeof storyHistoryEntrySchema>;
export const storyHistorySchema = z.object({
  history: z.record(storyFieldSchema, z.array(storyHistoryEntrySchema).max(5))
});
export const storyHistoryChangeSchema = z
  .object({
    field: storyFieldSchema,
    before: storyTextSchema,
    after: storyTextSchema
  })
  .strict();
export function storyValues(document: Record<string, unknown>): StoryValues {
  return Object.fromEntries(
    storyFields.map((key) => [key, typeof document[key] === "string" ? document[key] : ""])
  ) as StoryValues;
}
export const storyRequestSchema = z
  .object({
    fields: z
      .array(storyFieldSchema)
      .min(1)
      .max(4)
      .refine((fields) => new Set(fields).size === fields.length),
    context: storyValuesSchema
  })
  .strict();
export type StoryRequest = z.infer<typeof storyRequestSchema>;
export const storySaveSchema = z
  .object({
    patch: storyValuesSchema.partial().refine((patch) => Object.keys(patch).length > 0),
    expected: storyValuesSchema
  })
  .strict();
export const storyOutputSchema = z
  .object({
    aboutThisWork: storyTextSchema.nullable(),
    aCloserLook: storyTextSchema.nullable(),
    displayPossibilities: storyTextSchema.nullable(),
    fromTheCollection: storyTextSchema.nullable(),
    comparisonArtist: z.string().max(120).nullable()
  })
  .strict();
export const storyRunSchema = z.object({
  id: z.string().uuid(),
  state: z.enum(["pending", "running", "succeeded", "failed", "cancelled"]),
  fields: z.array(storyFieldSchema),
  createdAt: z.string(),
  progress: z.string(),
  preview: storyValuesSchema.partial(),
  result: storyValuesSchema.partial().nullable(),
  error: z.string().nullable()
});
export type StoryRun = z.infer<typeof storyRunSchema>;
export const storyWorkspaceSchema = z.object({
  saved: storyValuesSchema,
  runs: z.array(storyRunSchema)
});
